import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import QRCodeElement from '../QRCodeElement'
import { getScanQrSize } from './getScanQrSize'
import {
  QrExpandedCloseButton,
  QrExpandedContent,
  QrExpandedNotice,
  QrExpandedOverlay,
  QrExpandedQrWrapper
} from './QrExpandedOverlay.styles'

type QrExpandedDialogProps = {
  open: boolean
  value: string
  onClose: () => void
  closeLabel: string
  instruction?: string
  labelledBy?: string
}

export const QrExpandedDialog = ({
  open,
  value,
  onClose,
  closeLabel,
  instruction,
  labelledBy
}: QrExpandedDialogProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const [qrSize, setQrSize] = useState(() => getScanQrSize())

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleOverlayClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!open) return
    const onResize = () => setQrSize(getScanQrSize())
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open])

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        handleClose()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown, true)
      previousFocusRef.current?.focus()
    }
  }, [open, handleClose])

  if (!open || !value) return null

  return createPortal(
    <QrExpandedOverlay onClick={handleOverlayClick} role="presentation">
      <QrExpandedContent
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : instruction}
      >
        <QrExpandedCloseButton ref={closeButtonRef} type="button" onClick={handleClose} aria-label={closeLabel}>
          ×
        </QrExpandedCloseButton>
        <QrExpandedQrWrapper>
          <QRCodeElement value={value} showText={false} size={qrSize} />
        </QrExpandedQrWrapper>
        {instruction && <QrExpandedNotice id={labelledBy}>{instruction}</QrExpandedNotice>}
      </QrExpandedContent>
    </QrExpandedOverlay>,
    document.body
  )
}
